// ─────────────────────────────────────────────────────────────────────────────
// Race levels: a Hill Climb Racing style course. Instead of a grid of tiles, a
// race level is a ground line (one height per tile, joined by smooth curves)
// that students shape with hills, valleys, ramps and pits, plus things along
// it: coins, boost pads, checkpoints and the finish line. Players drive it in
// a car (components/RacePlayer.tsx) built in components/RaceEditor.tsx.
// ocean_game_check (20261031_race_levels.sql) checks the same rules as checkRace.
// ─────────────────────────────────────────────────────────────────────────────

import { drawBackground, type Theme } from './levels';

export type RaceLevel = {
  v: 1;
  mode: 'race';
  /** Length in tiles, and how tall the world is (the ground's lowest point is h; deeper is a pit). */
  w: number;
  h: number;
  /** The ground's height at each tile edge (w + 1 of them), in tenths of a tile from the top. */
  ground: number[];
  /** One letter per tile: '.' nothing, 'o' coin, 'z' boost pad, 'h' checkpoint, 'G' finish. */
  items: string;
  theme: Theme;
  /** Seconds to finish (0 = no limit). */
  time: number;
};

export const RACE_LIMITS = { minW: 50, maxW: 600, h: 16 };
export const PIT = 999;   // a ground height (in tenths) that means "no ground here"

export const RACE_ITEMS = [
  { ch: 'o', name: 'Coin', tip: 'Collect them' },
  { ch: 'z', name: 'Boost', tip: 'A speed boost when you drive over it' },
  { ch: 'h', name: 'Checkpoint', tip: 'You come back here after a crash' },
  { ch: 'G', name: 'Finish', tip: 'Cross it to win' },
] as const;

export const isRace = (x: unknown): boolean => !!x && typeof x === 'object' && (x as { mode?: unknown }).mode === 'race';

export function blankRace(w = 200): RaceLevel {
  const h = RACE_LIMITS.h;
  const ground: number[] = [];
  // Gentle rolling hills, eased in from a flat start.
  for (let x = 0; x <= w; x++) {
    const ease = Math.max(0, Math.min(1, (x - 6) / 14));
    ground.push(Math.round((h - 4 - ease * (Math.sin((x - 6) / 9) * 1.2 + Math.sin((x - 6) / 23) * 1.5)) * 10));
  }
  const items = ('.'.repeat(w)).split('');
  items[w - 4] = 'G';
  return { v: 1, mode: 'race', w, h, ground, items: items.join(''), theme: 'meadow', time: 0 };
}

export function checkRace(l: RaceLevel): string {
  if (l.ground.length !== l.w + 1 || l.items.length !== l.w) return 'This course is broken. Start a new one.';
  if (!l.items.includes('G')) return 'Put a Finish line where players win.';
  if (l.ground.slice(0, 6).some(g => g === PIT)) return 'Leave some ground at the start for the car.';
  return '';
}

export function cleanRace(x: unknown): RaceLevel | null {
  const v = (x && typeof x === 'object' ? x : null) as Record<string, unknown> | null;
  if (!v || v.mode !== 'race' || typeof v.w !== 'number' || !Array.isArray(v.ground) || typeof v.items !== 'string') return null;
  const w = Math.round(v.w);
  if (w < RACE_LIMITS.minW || w > RACE_LIMITS.maxW || v.ground.length !== w + 1 || v.items.length !== w) return null;
  const h = RACE_LIMITS.h;
  return {
    v: 1, mode: 'race', w, h,
    ground: v.ground.map(g => (g === PIT ? PIT : Math.max(10, Math.min((h + 4) * 10, Math.round(Number(g) || h * 10))))),
    items: v.items.replace(/[^.ozhG]/g, '.'),
    theme: v.theme === 'desert' || v.theme === 'sunset' || v.theme === 'night' ? v.theme : 'meadow',
    time: typeof v.time === 'number' && v.time > 0 ? Math.min(600, v.time) : 0,
  };
}

// ── The ground line ──────────────────────────────────────────────────────────

/** The ground's height (tiles from the top) at x, smoothly curved between the points; null over a pit. */
export function groundAt(l: RaceLevel, x: number): number | null {
  if (x < 0) x = 0;
  if (x > l.w) x = l.w;
  const i = Math.floor(x), f = x - i;
  const p = (k: number) => l.ground[Math.max(0, Math.min(l.w, k))];
  const p1 = p(i), p2 = p(i + 1);
  if (p1 === PIT || p2 === PIT) return null;
  // Catmull-Rom through the neighbours (a pit next door counts as level ground).
  const p0 = p(i - 1) === PIT ? p1 : p(i - 1), p3 = p(i + 2) === PIT ? p2 : p(i + 2);
  const f2 = f * f, f3 = f2 * f;
  const y = 0.5 * (2 * p1 + (-p0 + p2) * f + (2 * p0 - 5 * p1 + 4 * p2 - p3) * f2 + (-p0 + 3 * p1 - 3 * p2 + p3) * f3);
  return y / 10;
}

/** How steep the ground is at x (dy/dx, y down), or 0 over a pit. */
export function slopeAt(l: RaceLevel, x: number): number {
  const a = groundAt(l, x - 0.05), b = groundAt(l, x + 0.05);
  return a == null || b == null ? 0 : (b - a) / 0.1;
}

// ── Shaping tools (all return a new level) ───────────────────────────────────

const withGround = (l: RaceLevel, f: (g: number[]) => void): RaceLevel => { const g = [...l.ground]; f(g); return { ...l, ground: g }; };
const clampH = (l: RaceLevel, v: number) => Math.max(20, Math.min((l.h - 1) * 10, Math.round(v)));

/** Drag to draw: set the ground at x to y (tiles), easing the next tiles in so there are no cliffs. */
export function drawGround(l: RaceLevel, x: number, y: number): RaceLevel {
  const c = Math.round(x);
  return withGround(l, g => {
    for (let k = -2; k <= 2; k++) {
      const i = c + k; if (i < 0 || i > l.w) continue;
      const wgt = 1 - Math.abs(k) / 3;
      const cur = g[i] === PIT ? y * 10 : g[i];
      g[i] = clampH(l, cur + (y * 10 - cur) * wgt);
    }
  });
}

/** A smooth hill (amount > 0) or valley (< 0), 6 tiles each side. */
export function bump(l: RaceLevel, x: number, amount: number): RaceLevel {
  const c = Math.round(x);
  return withGround(l, g => {
    for (let k = -6; k <= 6; k++) {
      const i = c + k; if (i < 0 || i > l.w || g[i] === PIT) continue;
      g[i] = clampH(l, g[i] - amount * 10 * Math.exp(-(k * k) / 10));
    }
  });
}

/** A jump ramp: rises 2.5 tiles over 6 tiles, then drops straight back down. */
export function ramp(l: RaceLevel, x: number): RaceLevel {
  const c = Math.round(x);
  return withGround(l, g => {
    const base = g[Math.max(0, c - 1)] === PIT ? (l.h - 4) * 10 : g[Math.max(0, c - 1)];
    for (let k = 0; k <= 6; k++) { const i = c + k; if (i > l.w) break; g[i] = clampH(l, base - (k / 6) * 25); }
    if (c + 7 <= l.w) g[c + 7] = clampH(l, base);
  });
}

/** A pit, 3 tiles wide: fall in and you crash. */
export function pit(l: RaceLevel, x: number): RaceLevel {
  const c = Math.round(x);
  return withGround(l, g => { for (let k = 0; k < 3; k++) if (c + k > 5 && c + k < l.w) g[c + k] = PIT; });
}

/** Smooth the ground around x (and fill in a pit). */
export function smooth(l: RaceLevel, x: number): RaceLevel {
  const c = Math.round(x);
  return withGround(l, g => {
    for (let k = -2; k <= 2; k++) {
      const i = c + k; if (i <= 0 || i >= l.w) continue;
      const a = g[i - 1] === PIT ? null : g[i - 1], b = g[i + 1] === PIT ? null : g[i + 1];
      const nb = [a, b].filter((v): v is number => v != null);
      if (g[i] === PIT) { if (nb.length) g[i] = Math.round(nb.reduce((p, q) => p + q, 0) / nb.length); continue; }
      if (nb.length) g[i] = Math.round((g[i] * 2 + nb.reduce((p, q) => p + q, 0)) / (2 + nb.length));
    }
  });
}

export function setItem(l: RaceLevel, x: number, ch: string): RaceLevel {
  const i = Math.floor(x); if (i < 0 || i >= l.w) return l;
  const it = l.items.split('');
  it[i] = ch;
  return { ...l, items: it.join('') };
}

/** A longer or shorter course, keeping what's built (new ground continues the last height). */
export function resizeRace(l: RaceLevel, w: number): RaceLevel {
  w = Math.max(RACE_LIMITS.minW, Math.min(RACE_LIMITS.maxW, w));
  const ground = Array.from({ length: w + 1 }, (_, i) => (i <= l.w ? l.ground[i] : l.ground[l.w] === PIT ? (l.h - 4) * 10 : l.ground[l.w]));
  const items = Array.from({ length: w }, (_, i) => (i < l.w ? l.items[i] : '.'));
  if (!items.includes('G')) items[w - 4] = 'G';
  return { ...l, w, ground, items: items.join('') };
}

// ── Drawing ──────────────────────────────────────────────────────────────────

const INK = '#1e2633';
const DIRT: Record<Theme, [string, string]> = {
  meadow: ['#5cc24f', '#9b6b43'], desert: ['#f2c27a', '#d39a55'], sunset: ['#8fbf5a', '#7d5434'], night: ['#3f7a4a', '#4a3a2e'],
};

/** The ground from screen x 0 to W (camera at camX, camY; s px per tile). */
export function drawTerrain(g: CanvasRenderingContext2D, l: RaceLevel, s: number, camX: number, camY: number, W: number, H: number) {
  const [top, dirt] = DIRT[l.theme];
  const x0 = camX / s, x1 = (camX + W) / s;
  const step = 0.25;
  // Walk along the ground, filling each stretch between pits.
  let x = Math.max(0, Math.floor(x0 / step) * step - step);
  while (x <= Math.min(l.w, x1 + step)) {
    if (groundAt(l, x) == null) { x += step; continue; }
    const pts: [number, number][] = [];
    while (x <= Math.min(l.w, x1 + step)) {
      const y = groundAt(l, x);
      if (y == null) break;
      pts.push([x * s - camX, y * s - camY]);
      x += step;
    }
    if (pts.length < 2) continue;
    g.fillStyle = dirt;
    g.beginPath(); g.moveTo(pts[0][0], H + 10);
    for (const [px, py] of pts) g.lineTo(px, py);
    g.lineTo(pts[pts.length - 1][0], H + 10); g.closePath(); g.fill();
    // Grass on top, and a dark edge.
    g.strokeStyle = top; g.lineWidth = s * 0.35; g.lineJoin = 'round'; g.lineCap = 'round';
    g.beginPath(); pts.forEach(([px, py], k) => (k ? g.lineTo(px, py + s * 0.15) : g.moveTo(px, py + s * 0.15))); g.stroke();
    g.strokeStyle = 'rgba(30,38,51,0.5)'; g.lineWidth = Math.max(1, s / 16);
    g.beginPath(); pts.forEach(([px, py], k) => (k ? g.lineTo(px, py) : g.moveTo(px, py))); g.stroke();
  }
}

/** The things along the course: coins float above the ground; the rest stand on it. */
export function drawRaceItem(g: CanvasRenderingContext2D, ch: string, cx: number, groundY: number, s: number, t: number, lit = false) {
  g.save();
  g.lineWidth = Math.max(1, s / 16); g.strokeStyle = INK;
  if (ch === 'o') {
    const y = groundY - s * 1.4 + Math.sin(t * 3 + cx) * s * 0.08, wv = Math.abs(Math.cos(t * 3 + cx)) * 0.8 + 0.2;
    g.fillStyle = '#ffd23d'; g.beginPath(); g.ellipse(cx, y, s * 0.32 * wv, s * 0.32, 0, 0, Math.PI * 2); g.fill(); g.stroke();
  } else if (ch === 'z') {
    g.fillStyle = '#ff8a3d'; g.beginPath(); g.roundRect(cx - s * 0.6, groundY - s * 0.15, s * 1.2, s * 0.22, s * 0.08); g.fill(); g.stroke();
    g.fillStyle = '#fff';
    for (let k = 0; k < 2; k++) { const ox = cx - s * 0.3 + k * s * 0.35 + ((t * 2) % 1) * s * 0.1; g.beginPath(); g.moveTo(ox, groundY - s * 0.12); g.lineTo(ox + s * 0.15, groundY - s * 0.04); g.lineTo(ox, groundY + s * 0.04); g.closePath(); g.fill(); }
  } else if (ch === 'h') {
    g.fillStyle = '#c9d2de'; g.fillRect(cx - s * 0.05, groundY - s * 1.6, s * 0.1, s * 1.6);
    g.fillStyle = lit ? '#2fcf6a' : '#8a94a3';
    g.beginPath(); g.moveTo(cx + s * 0.05, groundY - s * 1.6); g.lineTo(cx + s * 0.75, groundY - s * 1.35); g.lineTo(cx + s * 0.05, groundY - s * 1.1); g.closePath(); g.fill(); g.stroke();
  } else if (ch === 'G') {
    // A checkered finish arch.
    const hgt = s * 3, wd = s * 0.25;
    for (const side of [-1, 1]) { g.fillStyle = '#e8e8e8'; g.fillRect(cx + side * s * 1.2 - wd / 2, groundY - hgt, wd, hgt); g.strokeRect(cx + side * s * 1.2 - wd / 2, groundY - hgt, wd, hgt); }
    const bw = s * 2.6, bh = s * 0.5, n = 8;
    for (let k = 0; k < n; k++) for (let r = 0; r < 2; r++) { g.fillStyle = (k + r) % 2 ? '#1e2633' : '#fff'; g.fillRect(cx - bw / 2 + (k * bw) / n, groundY - hgt + (r * bh) / 2, bw / n, bh / 2); }
    g.strokeRect(cx - bw / 2, groundY - hgt, bw, bh);
  }
  g.restore();
}

/** A small picture of a whole course. */
export function raceThumb(l: RaceLevel, maxW = 360): string {
  const W = maxW, s = W / l.w, H = Math.round(l.h * s * 1.6);
  const c = document.createElement('canvas'); c.width = W; c.height = Math.max(40, H);
  const g = c.getContext('2d')!;
  const sy = c.height / l.h;
  drawBackground(g, l.theme, W, c.height, 0, 0);
  // Squash it vertically less than horizontally so hills still show.
  g.save(); g.scale(1, sy / s);
  drawTerrain(g, l, s, 0, 0, W, c.height * (s / sy));
  g.restore();
  for (let i = 0; i < l.w; i++) {
    const ch = l.items[i]; if (ch === '.') continue;
    const gy = groundAt(l, i + 0.5); if (gy == null) continue;
    g.fillStyle = ch === 'G' ? '#1e2633' : ch === 'h' ? '#2fcf6a' : ch === 'z' ? '#ff8a3d' : '#ffd23d';
    g.fillRect(i * s, gy * sy - (ch === 'G' ? 10 : 4), Math.max(2, s), ch === 'G' ? 10 : 4);
  }
  return c.toDataURL('image/png');
}
